import { Document, Page, pdfjs } from "react-pdf";
import { SignerState } from "./ParticipantScreenService";
import { RequestFile, RequestParticipant } from "models/interfaces";
import { Box, CircularProgress, Typography, Divider } from "@mui/material";
import strings from "global/constants/StringConstants";
import { isTruthy } from "helpers/methods";
import { useRef, useState, useEffect, useCallback, ReactNode } from "react";
import ViewFields from "./ViewFields";
import { getDocumentUrl } from "../SignRequestService";
import {
  isAnySignerField,
  isFieldFillableBy,
  isFieldVisibleTo,
} from "../SignRequestHelper";
import { useAppSelector } from "hooks/use-app-select";
import { RootState } from "utils/store";
import tokens from "./SignerExperience.tokens";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export type PDFViewerProps = {
  documentData?: any;
  onDocumentLoad?: (doc: any) => void;
  onPageClick?: (params: {
    pageNumber: number;
    numPages: number;
    originalEvent: React.MouseEvent<HTMLDivElement, MouseEvent>;
    pageHeight: number;
    pageWidth: number;
    pageX: number;
    pageY: number;
  }) => void;
  docLoadSuccess: boolean;
  setDocLoadSuccess: Function;
  docLoading: boolean;
  setDocLoading: Function;
  signerSelectedFile: any;
  setSignerSelectedFile: Function;
  requestToken?: string;
  state: SignerState;
  dispatch: Function;
  loading: boolean;
  setLoading: Function;
  selectedFile: RequestFile;
  setSelectedFile: Function;
  fileSrc: string;
  currentPageNumber: number;
  setCurrentPageNumber: Function;
  handleFileChange: Function;
  handleSubmit: Function;
  downloadFile: Function;
  currentReviewer: RequestParticipant;
  handleApproveRejectRequest: Function;
  reqToken: string;
  currentFieldOnFocus: number;
  setCurrentFieldOnFocus: Function;
  moveToFieldLocation: Function;
  signingJourneyOn: boolean;
  setSigningJourneyOn: Function;
  attachSingleCertificate: boolean;
  allFieldsFilled: boolean;
  isMobileScreen?: boolean;
  disableFields?: boolean;
  /**
   * Desktop signing canvas from the design: lavender backdrop, 820px content
   * column, and the PDF pages rendered into a single rounded white sheet.
   * Mobile keeps the plain full-bleed viewer.
   */
  canvasChrome?: boolean;
  /**
   * Toolbar rendered above the page sheet (document + page steppers). Receives
   * live paging state so the stepper can drive the scroll container.
   */
  renderToolbar?: (context: {
    numPages: number;
    currentPage: number;
    goToPage: (page: number) => void;
  }) => ReactNode;
};

const { palette, canvas: canvasTokens } = tokens;

const ParticipantDocumentViewer = ({
  onDocumentLoad,
  onPageClick,
  setDocLoadSuccess,
  docLoadSuccess,
  docLoading,
  setDocLoading,
  signerSelectedFile,
  setSignerSelectedFile,
  requestToken,
  state,
  loading,
  setLoading,
  selectedFile,
  setSelectedFile,
  fileSrc,
  currentPageNumber,
  setCurrentPageNumber,
  handleFileChange,
  handleSubmit,
  downloadFile,
  currentReviewer,
  handleApproveRejectRequest,
  reqToken,
  currentFieldOnFocus,
  setCurrentFieldOnFocus,
  moveToFieldLocation,
  signingJourneyOn,
  setSigningJourneyOn,
  attachSingleCertificate,
  allFieldsFilled,
  dispatch,
  isMobileScreen,
  disableFields,
  canvasChrome,
  renderToolbar,
}: PDFViewerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  // The recipient this page was opened for; any-signer fields are drawn once,
  // under them, rather than once per pending signer.
  const { selectedParticipant } = useAppSelector(
    (state: RootState) => state.app,
  );
  const currentSigner = (state.signRequest.signers ?? []).find(
    (s: RequestParticipant) => s.email === selectedParticipant?.email,
  );
  const [visiblePages, setVisiblePages] = useState<number[]>([1]);
  const [width, setWidth] = useState(0);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const [pagesHeight, setPagesHeight] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Track the PDF URL to only reset loading state when URL actually changes
  const pdfUrlRef = useRef<string>("");
  useEffect(() => {
    const currentUrl =
      signerSelectedFile?.unsignedPdfUrl ||
      (typeof signerSelectedFile === "string" ? signerSelectedFile : "");
    // Only reset loading state if the URL actually changed (not just object reference)
    if (currentUrl && currentUrl !== pdfUrlRef.current) {
      pdfUrlRef.current = currentUrl;
      if (isTruthy(signerSelectedFile)) {
        setVisiblePages([1]);
        setCurrentPage(1);
        setDocLoading(false);
        setDocLoadSuccess(false);
      }
    }
  }, [signerSelectedFile?.unsignedPdfUrl]);

  useEffect(() => {
    if (pageRef.current) {
      setPagesHeight(pageRef.current.offsetHeight);
    }
  }, [numPages, visiblePages]);

  useEffect(() => {
    const handleResize = () => {
      const target = containerRef.current;
      if (!target) return;
      // The sheet is the scroller in the design canvas, so `clientWidth` — which
      // already excludes its border and scrollbar — is exactly the page width.
      // Otherwise: mobile renders pages nearly full-width (matches the editor);
      // desktop keeps a larger inset.
      const measured = canvasChrome
        ? target.clientWidth
        : target.offsetWidth - (isMobileScreen ? 4 : 40);
      if (measured > 0) setWidth(measured);
    };

    handleResize();
    // ResizeObserver re-measures once the container actually has its final
    // width (offsetWidth can be 0/stale at mount, and the gray canvas can
    // change size), so the page always fills the available width.
    let observer: ResizeObserver | undefined;
    let frame = 0;
    const observed = containerRef.current;
    if (typeof ResizeObserver !== "undefined" && observed) {
      observer = new ResizeObserver(() => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(handleResize);
      });
      observer.observe(observed);
    }
    window.addEventListener("resize", handleResize);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileScreen, canvasChrome]);

  /** Page whose top edge last crossed the top of the sheet's viewport. */
  const syncCurrentPage = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const pages = container.querySelectorAll<HTMLElement>(".react-pdf__Page");
    if (!pages.length) return;
    const marker = container.getBoundingClientRect().top + 40;
    let active = 1;
    pages.forEach((page) => {
      if (page.getBoundingClientRect().top <= marker) {
        active = Number(page.dataset.pageNumber) || active;
      }
    });
    setCurrentPage((previous) => (previous === active ? previous : active));
  }, []);

  const goToPage = useCallback(
    (page: number) => {
      const container = containerRef.current;
      if (!container || page < 1 || (numPages && page > numPages)) return;
      // Pages past the lazy-load window aren't mounted yet — extend the window
      // first, then scroll on the next frame once they render.
      setVisiblePages((previous) =>
        previous.length >= page
          ? previous
          : Array.from({ length: page }, (_, index) => index + 1),
      );
      let attempts = 0;
      const scroll = () => {
        const $page = container.querySelector<HTMLElement>(
          `.react-pdf__Page[data-page-number="${page}"]`,
        );
        if (!$page) {
          // The page is still rendering. Give it a bounded number of frames
          // rather than spinning forever if it never mounts.
          if (attempts++ < 120) requestAnimationFrame(scroll);
          return;
        }
        const offset =
          $page.getBoundingClientRect().top -
          container.getBoundingClientRect().top;
        container.scrollTo({
          top: container.scrollTop + offset,
          behavior: "smooth",
        });
        setCurrentPage(page);
      };
      requestAnimationFrame(scroll);
    },
    [numPages],
  );

  const handleDocumentLoad = (doc: any) => {
    if (doc.numPages < 10) {
      setVisiblePages(
        Array.from({ length: doc.numPages }, (_, index) => index + 1),
      );
    } else if (doc.numPages > 1) {
      const maxPage = isTruthy(selectedFile.fields)
        ? selectedFile.fields.reduce((max, obj) => {
            return obj.page > max.page ? obj : max;
          }, selectedFile.fields[0]).page
        : 2;
      setVisiblePages(
        Array.from({ length: maxPage + 1 }, (_, index) => index + 1),
      );
    }
    setNumPages(doc.numPages);
    setCurrentPage(1);
    onDocumentLoad?.(doc);
    setDocLoadSuccess(true);
  };

  const handlePageClick = (
    event: React.MouseEvent<HTMLDivElement>,
    pageNumber: number,
  ) => {
    const pageElement = (event.target as HTMLElement).closest(
      ".react-pdf__Page",
    );

    if (pageElement) {
      const { top, left, width, height } = pageElement.getBoundingClientRect();
      const pageX = event.clientX - left;
      const pageY = event.clientY - top;

      onPageClick?.({
        pageNumber,
        numPages,
        originalEvent: event,
        pageHeight: height,
        pageWidth: width,
        pageX,
        pageY,
      });
    }
  };

  const handleScroll = () => {
    syncCurrentPage();
    if (!containerRef.current || isLoadingPages) return;

    const { scrollTop, clientHeight, scrollHeight } = containerRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setIsLoadingPages(true);

      setTimeout(() => {
        setVisiblePages((prevPages) => {
          const lastLoadedPage = prevPages[prevPages.length - 1];
          const nextPages = Array.from(
            { length: numPages < 5 ? numPages : 5 },
            (_, i) => lastLoadedPage + i + 1,
          ).filter((page) => page <= numPages);

          return [...prevPages, ...nextPages];
        });

        setIsLoadingPages(false);
      }, 1000);
    }
  };

  const documentBody = (
    <>
      {docLoading ? (
        <Box
          role="status"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress aria-hidden="true" />
          <Typography ml={2}>Loading document...</Typography>
        </Box>
      ) : (
        <Document
          key={
            isTruthy(signerSelectedFile.unsignedPdfUrl)
              ? signerSelectedFile.unsignedPdfUrl
              : signerSelectedFile
          }
          file={
            isTruthy(signerSelectedFile.unsignedPdfUrl)
              ? signerSelectedFile.unsignedPdfUrl
              : signerSelectedFile
          }
          onLoadSuccess={handleDocumentLoad}
          onLoadError={async () => {
            try {
              const response = await getDocumentUrl(
                signerSelectedFile,
                true,
                requestToken!,
                true,
              );
              const newUrl = response?.message;
              if (!newUrl || newUrl === signerSelectedFile.unsignedPdfUrl) {
                return;
              }
              setSignerSelectedFile({
                ...signerSelectedFile,
                unsignedPdfUrl: newUrl,
              });
            } catch (err) {
              console.error("Failed to refresh PDF URL", err);
            }
          }}
          error={"An Error occurred while showing pdf"}
          loading={() => (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="80vh"
            >
              <CircularProgress />
              <Typography ml={2}>Loading document...</Typography>
            </Box>
          )}
        >
          {visiblePages.map((pageNumber, i) => (
            <Box key={`page-${i + 1}`} sx={{ display: "contents" }}>
              <Page
                pageNumber={i + 1}
                width={canvasChrome ? width : isMobileScreen ? width : width - 40}
                renderAnnotationLayer={false}
                renderTextLayer={false}
                onClick={(e: any) => handlePageClick(e, i + 1)}
              >
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: "transparent",
                    color: "text.primary",
                    borderRadius: "5px",
                    zIndex: 10,
                    width: "100%",
                  }}
                >
                  {/* The design canvas replaces the per-page caption with the
                      toolbar's page stepper, so pages are only separated by a
                      hairline inside the sheet. */}
                  {!canvasChrome && (
                    <Typography
                      variant="caption"
                      align="center"
                      display="block"
                      sx={{
                        fontSize: {
                          xs: "0.3rem",
                          sm: "0.7rem",
                          md: "0.8rem",
                          lg: "0.9rem",
                          xl: "1rem",
                        },
                      }}
                    >
                      Page {i + 1} of {numPages}
                    </Typography>
                  )}
                  {(!canvasChrome || i + 1 < visiblePages.length) && (
                    <Divider
                      sx={{
                        width: "100%",
                        ...(canvasChrome
                          ? { borderColor: palette.borderLight }
                          : {}),
                      }}
                    />
                  )}
                </Box>
              </Page>

              {(state.signRequest.signers ?? []).map((signer: any) => {
                if (
                  (signer.status === strings.pending ||
                    signer.status === strings.delay) &&
                  selectedFile.unsignedPdfUrl
                ) {
                  const currentFileInState = state.signRequest.files.find(
                    (f: any) =>
                      f.unsignedPdf === selectedFile.unsignedPdf ||
                      f.unsignedDoc === selectedFile.unsignedDoc,
                  );
                  const fieldsToRender =
                    currentFileInState?.fields || selectedFile?.fields || [];

                  return fieldsToRender.map(
                    (field: any, fieldIndex: number) => {
                      if (!isTruthy(selectedFile.dimensions)) {
                        return null;
                      }
                      const anySigner = isAnySignerField(field);
                      const isCurrentSigner = anySigner
                        ? (currentSigner?.id ?? signer.id) === signer.id
                        : isFieldVisibleTo(field, signer.id);
                      const isCurrentField = field.id === currentFieldOnFocus;
                      // An any-signer field someone else already filled is
                      // shown, but locked, for everyone after them.
                      const fieldToRender =
                        anySigner &&
                        !isFieldFillableBy(field, signer.id, signer.email)
                          ? { ...field, readOnly: true }
                          : field;
                      return (
                        <Box key={fieldIndex}>
                          {isCurrentSigner &&
                            // field.id &&
                            !docLoading &&
                            docLoadSuccess &&
                            isTruthy(fieldsToRender) &&
                            field.page + 1 === pageNumber && (
                              <ViewFields
                                isCurrentField={isCurrentField}
                                state={state}
                                field={fieldToRender}
                                setDocLoading={setDocLoading}
                                setLoading={setLoading}
                                dispatch={dispatch}
                                selectedFile={selectedFile}
                                setSelectedFile={setSelectedFile}
                                signer={signer}
                                currentReviewer={currentReviewer}
                                signImage={state.signImage}
                                initialsImage={state.initialsImage}
                                currentPageNumber={currentPageNumber}
                                frameX={0}
                                frameY={0}
                                setCurrentFieldOnFocus={setCurrentFieldOnFocus}
                                currentFieldOnFocus={currentFieldOnFocus}
                                moveToFieldLocation={moveToFieldLocation}
                                signingJourneyOn={signingJourneyOn}
                                pagesHeight={pagesHeight}
                                isMobileScreen={isMobileScreen}
                                disableFields={disableFields ?? false}
                              />
                            )}
                        </Box>
                      );
                    },
                  );
                }
                return null;
              })}
            </Box>
          ))}

          {isLoadingPages && visiblePages.length < numPages && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={2}
            >
              <CircularProgress size={24} />
              <Typography ml={2}>Loading more pages...</Typography>
            </Box>
          )}
        </Document>
      )}
    </>
  );

  if (canvasChrome) {
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          pt: canvasTokens.paddingTop,
          px: canvasTokens.paddingX,
          pb: canvasTokens.paddingBottom,
          backgroundColor: canvasTokens.background,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: canvasTokens.contentMaxWidth,
            mx: "auto",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {renderToolbar?.({ numPages, currentPage, goToPage })}
          {/* The page sheet is the scroll container *and* the positioning
              parent for the field overlays: its content origin is page 1's
              origin, which is what ViewFields' `page * height` offset assumes.
              Only `padding-bottom` is safe here — padding on any other edge
              would move that origin. */}
          <Box
            id="pdf_box"
            ref={containerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              position: "relative",
              pb: canvasTokens.sheetScrollPaddingBottom,
              backgroundColor: palette.white,
              border: canvasTokens.pageBorder,
              borderRadius: canvasTokens.pageRadius,
              boxShadow: canvasTokens.pageShadow,
              "&::-webkit-scrollbar": { width: "10px" },
              "&::-webkit-scrollbar-thumb": {
                border: "2px solid rgba(0, 0, 0, 0)",
                backgroundClip: "padding-box",
                borderRadius: "9999px",
                backgroundColor: "rgba(0, 0, 0, 0.2)",
              },
              "&::-webkit-scrollbar-track": { background: "transparent" },
            }}
          >
            {documentBody}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      id="pdf_box"
      ref={containerRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      height="100%"
      maxWidth="100%"
      onScroll={handleScroll}
      position="relative"
      sx={{
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        px: isMobileScreen ? 1 : 2,
        // Vertically center a single-page document; auto margins collapse to 0
        // when the content overflows, so multi-page stays scrollable from top.
        "& .react-pdf__Document": {
          marginTop: numPages <= 1 ? "auto" : 0,
          marginBottom: numPages <= 1 ? "auto" : 0,
        },
        "&::-webkit-scrollbar": {
          display: "block !important",
          width: isMobileScreen ? "8px" : "15px",
        },
        "&::-webkit-scrollbar-thumb": {
          border: "2px solid rgba(0, 0, 0, 0)",
          backgroundClip: "padding-box",
          borderRadius: "9999px",
          backgroundColor: "rgba(0, 0, 0, 0.2)",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
      }}
    >
      {documentBody}
    </Box>
  );
};

export default ParticipantDocumentViewer;
